import React, { useLayoutEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import Header from "@/components/site/Header";
import Hero from "@/components/site/Hero";
import TrustStrip from "@/components/site/TrustStrip";
import PricingSnapshot from "@/components/site/PricingSnapshot";
import Services from "@/components/site/Services";
import TowingHub from "@/components/site/TowingHub";
import WhyUs from "@/components/site/WhyUs";
import Gallery from "@/components/site/Gallery";
import ServiceAreas from "@/components/site/ServiceAreas";
import Testimonials from "@/components/site/Testimonials";
import PriceList from "@/components/site/PriceList";
import FAQ from "@/components/site/FAQ";
import Contact from "@/components/site/Contact";
import Footer from "@/components/site/Footer";
import MobileCallBar from "@/components/site/MobileCallBar";
import ReviewPage from "@/components/ReviewPage";
import ReviewsPage from "@/components/ReviewsPage";
import SuburbPage from "@/components/SuburbPage";
import ShopPage from "@/components/ShopPage";
import ServicePage from "@/components/ServicePage";
import BuildsPage from "@/components/BuildsPage";
import BuildPage from "@/components/BuildPage";
import PoliciesPage from "@/components/PoliciesPage";
import PrivacyPage from "@/components/PrivacyPage";
import TowingDealsPage from "@/components/TowingDealsPage";
import BlogIndex from "@/components/BlogIndex";
import BlogPost from "@/components/BlogPost";
import AboutPage from "@/components/AboutPage";
import { SITE } from "@/site.config";
import { INDEXABLE_SUBURBS, RETIRED_SUBURB_SLUGS, SUBURB_PATH } from "@/suburbs";

function HomePage() {
  return (
    <div className="bg-black text-white">
      <Header />
      <main>
        <Hero />
        <TrustStrip />
        <PricingSnapshot />
        <Services />
        <TowingHub />
        <PriceList />
        <WhyUs />
        <Gallery />
        <ServiceAreas />
        <Testimonials />
        <FAQ />
        <Contact />
      </main>
      <Footer />
    </div>
  );
}

function ScrollToTop() {
  const { pathname, hash } = useLocation();
  useLayoutEffect(() => {
    if ("scrollRestoration" in window.history) window.history.scrollRestoration = "manual";
  }, []);
  useLayoutEffect(() => {
    const html = document.documentElement;
    if (hash) {
      let tries = 0;
      const jump = () => {
        const el = document.getElementById(hash.slice(1));
        if (el) el.scrollIntoView({ block: "start" });
        else if (tries++ < 30) setTimeout(jump, 100);
      };
      jump();
      return;
    }
    const prev = html.style.scrollBehavior;
    html.style.scrollBehavior = "auto";
    window.scrollTo(0, 0);
    html.scrollTop = 0;
    document.body.scrollTop = 0;
    requestAnimationFrame(() => {
      html.style.scrollBehavior = prev;
    });
  }, [pathname, hash]);
  return null;
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <ScrollToTop />
        <MobileCallBar />
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/review" element={<ReviewPage />} />
          <Route path="/reviews" element={<ReviewsPage />} />
          <Route path="/shop" element={<ShopPage />} />
          <Route path="/services/:slug" element={<ServicePage />} />
          <Route path="/builds" element={<BuildsPage />} />
          <Route path="/builds/:slug" element={<BuildPage />} />
          <Route path="/policies" element={<PoliciesPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/towing-deals" element={<TowingDealsPage />} />
          <Route path="/blog" element={<BlogIndex />} />
          <Route path="/blog/:slug" element={<BlogPost />} />
          <Route path="/about" element={<AboutPage />} />
          {/* URL alias redirects — preserve link equity for expected URLs
             that map onto existing canonical service pages. */}
          <Route
            path="/services/redarc-tow-pro-installation-sunshine-coast"
            element={
              <Navigate to="/services/redarc-brake-controllers" replace />
            }
          />
          <Route
            path="/services/electric-brake-controllers-sunshine-coast"
            element={
              <Navigate to="/services/redarc-brake-controllers" replace />
            }
          />
          <Route
            path="/services/dual-battery-systems-sunshine-coast"
            element={<Navigate to="/services/dual-battery-systems" replace />}
          />
          <Route
            path="/services/auto-air-conditioning-sunshine-coast"
            element={<Navigate to="/services/auto-air-conditioning" replace />}
          />
          <Route
            path="/services/uhf-installation-sunshine-coast"
            element={
              <Navigate
                to="/services/uhf-radio-installation-sunshine-coast"
                replace
              />
            }
          />
          <Route
            path="/services/driving-light-installation-sunshine-coast"
            element={
              <Navigate
                to="/services/driving-lights-spotlights-installation-sunshine-coast"
                replace
              />
            }
          />
          <Route
            path="/services/4wd-auto-electrical-sunshine-coast"
            element={
              <Navigate
                to="/services/4x4-auto-electrical-sunshine-coast"
                replace
              />
            }
          />
          {INDEXABLE_SUBURBS.map((s) => (
            <Route
              key={s.slug}
              path={SUBURB_PATH(s.slug)}
              element={<SuburbPage suburb={s} />}
            />
          ))}
          {/* Retired suburb doorway pages — 301-style redirect to the primary
              geographic authority page for auto-electrical searches on the
              Sunshine Coast. This preserves URL equity from any external
              backlinks to old suburb URLs. */}
          {RETIRED_SUBURB_SLUGS.map((slug) => (
            <Route
              key={`retired-${slug}`}
              path={SUBURB_PATH(slug)}
              element={
                <Navigate to="/services/auto-electrician-sunshine-coast" replace />
              }
            />
          ))}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
